import { Button, CustomClaimValue, TrackerConfig, TrackerConfigs, TrackerConfigType } from "@app/shared";
import { FC, useState } from "react";
import { useConfirmDialog, useFetch, useModel } from "../../../hooks";
import { EnumSelect, IconLink, Icons, Input, InputRow, SortableTable, TextArea } from "../../shared";
import { AuthorizeView } from "../../shared/AuthorizeView";
import { toastWarning } from "../../../helpers";

export const TrackerLibrary: FC = (props) => {
    const [configs, , configApi] = useFetch(TrackerConfigs.getAll);
    const confirmDialog = useConfirmDialog();
    const [curConfig, configHelpers] = useModel(TrackerConfig);
    const [isEditing, setIsEditing] = useState(false);

    const handleDeleteOnClick = async (config: TrackerConfig) => {
        try {
            await TrackerConfigs.deleteConfig(config.id);
            configApi.refreshData();
        } catch (error: any) {
            if (error.message && error.message.includes("being used by")) {
                confirmDialog.show("Tracker Config In Use", `${error.message}\n\nDo you want to force delete this tracker config? This will remove it from all chirp configs using it.`, "Force Delete", async () => {
                    await TrackerConfigs.forceDeleteConfig(config.id);
                    await configApi.refreshData();
                });
            } else {
                throw error;
            }
        }
    };

    const handleOnRowClick = (config: TrackerConfig) => {
        if (isEditing) {
            configHelpers.replaceModel(config);
            return;
        }
        configHelpers.replaceModel(config);
        setIsEditing(true);
    };

    const handleCancelEditOnClick = () => {
        setIsEditing(false);
        configApi.refreshData();
    };

    const handleAddNewOnClick = () => {
        configHelpers.replaceModel(new TrackerConfig());
        setIsEditing(true);
    };

    const handleSaveOnClick = async () => {
        // Validate JSON before saving
        if (curConfig.trackerConfig && curConfig.trackerConfig.trim()) {
            try {
                JSON.parse(curConfig.trackerConfig);
            } catch (e) {
                toastWarning("Invalid JSON format in Tracker Config field. Please correct the JSON and try again.");
                return;
            }
        }
        await TrackerConfigs.save(curConfig as TrackerConfig);
        setIsEditing(false);
        configApi.refreshData();
    };

    const editView = (
        <>
            <InputRow>
                <Input label="Name" {...configHelpers.bindingsFor("name")} />
            </InputRow>
            <InputRow>
                <Input label="Description" {...configHelpers.bindingsFor("description")} />
            </InputRow>
            <InputRow>
                <EnumSelect label="Type" enumType={TrackerConfigType} {...configHelpers.bindingsFor("type")} />
            </InputRow>
            <InputRow>
                <TextArea label="Tracker Config JSON" rows={20} {...configHelpers.bindingsFor("trackerConfig")} placeholder="Enter valid JSON configuration for the tracker..." />
            </InputRow>
            <div className="absolute bottom-0 right-10 p-5 flex flex-row gap-2">
                <Button onClick={handleSaveOnClick}>Save</Button>
                <Button onClick={handleCancelEditOnClick}>Cancel</Button>
            </div>
        </>
    );

    const tableView = (
        <>
            <SortableTable
                onRowClick={handleOnRowClick}
                data={configs}
                emptyValue="No Tracker Configs Found"
                isLoading={configApi.isLoading}
                columns={[
                    { header: "Name", dataKey: "name", width: 200 },
                    { header: "Description", dataKey: "description", width: 250 },
                    { 
                        header: "Type", 
                        dataKey: "type", 
                        width: 100,
                        renderFunc: (config) => config?.type === TrackerConfigType.Radar ? "Radar" : "Video"
                    },
                    {
                        header: (
                            <>
                                <IconLink Icon={Icons.PlusCircle} onClick={handleAddNewOnClick}>
                                    Add New
                                </IconLink>
                            </>
                        ),
                        renderFunc: (config) => (
                            <IconLink
                                Icon={Icons.Trash}
                                onClick={(e) => {
                                    handleDeleteOnClick(config!);
                                    e.stopPropagation();
                                }}
                            >
                                Delete
                            </IconLink>
                        ),
                        width: 150,
                    },
                ]}
            />
            <div className="flex place-items-center justify-center p-5">
                <Button onClick={handleAddNewOnClick}>Add New Tracker Config</Button>
            </div>
        </>
    );

    return (
        <AuthorizeView claim={CustomClaimValue.SuperAdmin}>
            {isEditing ? editView : tableView}
            {confirmDialog.renderDialog()}
        </AuthorizeView>
    );
};
